/**
 * BottomNavigation Temp Version - Shopping Mode
 * PG 심사용으로 '경매' 관련 워딩을 '구매'로 변경하고 라이브 경매 탭 제거
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useUserAuth } from '../context/UserAuthContext';
import { useLang } from '../context/LangContext';
import { useAdditionalInfoCheck } from '../hooks/useAdditionalInfoCheck';
import AdditionalInfoModal from '../components/AdditionalInfoModal';

const BottomNavContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 8px 0 calc(8px + env(safe-area-inset-bottom));
  z-index: 1000;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);

  @media (min-width: 768px) {
    display: none; /* 데스크톱에서는 숨김 */
  }
`;

const NavItem = styled.button<{ $isActive?: boolean }>`
  background: none;
  border: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  cursor: pointer;
  transition: color 0.2s ease;
  color: ${({ $isActive }) => ($isActive ? '#69b5b0' : '#b3b3b3')};
  font-size: 12px;
  font-weight: ${({ $isActive }) => ($isActive ? '600' : '400')};

  &:hover {
    color: #69b5b0;
  }
`;

const NavIcon = styled.img`
  width: 24px;
  height: 24px;
  margin-bottom: 4px;
`;

interface IconProps {
  isActive: boolean;
  iconName: string;
}

const Icon: React.FC<IconProps> = ({ isActive, iconName }) => (
  <NavIcon 
    src={`/nav/${iconName}${isActive ? '_on' : ''}.svg`} 
    alt={iconName} 
  />
);

interface BottomNavigationProps {
  currentPage?: 'auction' | 'home' | 'favorites' | 'mypage'; // live-auctions 제거
  onLoginRequired?: () => void; // 로그인 필요시 호출할 콜백
}

const BottomNavigationTemp: React.FC<BottomNavigationProps> = ({
  currentPage = 'home',
  onLoginRequired,
}) => {
  const navigate = useNavigate();
  const { isLoggedIn } = useUserAuth();
  const { lang } = useLang();

  // 추가 정보 체크 훅
  const auctionInfoCheck = useAdditionalInfoCheck({
    onNavigate: () => navigate('/auction')
  });

  const favoritesInfoCheck = useAdditionalInfoCheck({
    onNavigate: () => navigate('/favorites')
  });

  const getText = (key: string): string => {
    const translations: Record<string, Record<string, string>> = {
      auction: { ko: '구매', en: 'Shop' }, // 경매 -> 구매
      home: { ko: '홈', en: 'Home' },
      favorites: { ko: '즐겨찾기', en: 'Favorites' },
      mypage: { ko: '마이페이지', en: 'My Page' }
    };
    return translations[key]?.[lang] || key;
  };

  const handleNavigation = (path: string, requiresLogin: boolean = false, needsAdditionalInfoCheck: boolean = false) => {
    if (requiresLogin && !isLoggedIn) {
      onLoginRequired?.();
      return;
    }

    // 추가 정보 체크가 필요한 경우
    if (needsAdditionalInfoCheck) {
      if (path === '/auction') {
        auctionInfoCheck.checkAndNavigate();
      } else if (path === '/favorites') {
        favoritesInfoCheck.checkAndNavigate();
      }
      return;
    }

    navigate(path);
  };

  return (
    <>
      <BottomNavContainer>
        {/* 라이브 경매 탭 제거 */}
        
        <NavItem
          $isActive={currentPage === 'auction'}
          onClick={() => handleNavigation('/auction', true, true)}
        >
          <Icon isActive={currentPage === 'auction'} iconName="auction" />
          {getText('auction')}
        </NavItem>
        
        <NavItem
          $isActive={currentPage === 'home'}
          onClick={() => handleNavigation('/')}
        >
          <Icon isActive={currentPage === 'home'} iconName="home" />
          {getText('home')}
        </NavItem>
        
        <NavItem
          $isActive={currentPage === 'favorites'}
          onClick={() => handleNavigation('/favorites', true, true)}
        >
          <Icon isActive={currentPage === 'favorites'} iconName="favorite" />
          {getText('favorites')}
        </NavItem>
        
        <NavItem
          $isActive={currentPage === 'mypage'}
          onClick={() => handleNavigation('/mypage', false)}
        >
          <Icon isActive={currentPage === 'mypage'} iconName="my" />
          {getText('mypage')}
        </NavItem>
      </BottomNavContainer>

      {/* 경매 페이지 추가 정보 모달 */}
      <AdditionalInfoModal
        isOpen={auctionInfoCheck.showAdditionalInfoModal}
        onClose={auctionInfoCheck.handleCloseModal}
        onGoToAdditionalInfo={auctionInfoCheck.handleGoToAdditionalInfo}
      />

      {/* 즐겨찾기 페이지 추가 정보 모달 */}
      <AdditionalInfoModal
        isOpen={favoritesInfoCheck.showAdditionalInfoModal}
        onClose={favoritesInfoCheck.handleCloseModal}
        onGoToAdditionalInfo={favoritesInfoCheck.handleGoToAdditionalInfo}
      />
    </>
  );
};

export default BottomNavigationTemp;
